// @flow
import React, { Fragment, useContext, useState, type Node } from 'react';
import classnames from 'classnames';

import { ID_MODAL_HEADER__SHOPPING__WEBCALLBACK_TRIGGER } from 'Components/utils/identifiers/constants';
import { t } from 'Src/i18n';
import { ReactPageContext } from 'Context/pageContext';
import { WEB_CALLBACK_OPEN } from 'Tracking/constants';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { Modal } from 'Components/frameworks/modal/Modal.jsx';
import { SidePanel } from 'Components/frameworks/side-panel/SidePanel.jsx';
import { themes } from 'Components/frameworks/block-link/BlockLink.themes';

import { WebCallbackForm } from './WebCallbackForm.jsx';
import { CALLBACK_STATUS } from './WebCallbackForm.utils';

export type WebCallbackTriggerProps = {
  children: Node,
  theme: string,
  trackingData: {|
    actionGA: string,
  |},
};

export function WebCallbackTrigger({
  trackingData,
  children,
  theme = 'default',
}: WebCallbackTriggerProps) {
  const { isTablet, isAppMounted } = useContext(ResponsiveContext);
  const pageContext = useContext(ReactPageContext);

  const track = useTracking();

  const [isOpen, setIsOpen] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState(CALLBACK_STATUS.UNSCHEDULED);

  const webCallbackModal = isOpen ? (
    <Modal
      ariaLabel={t('webCallback.title')}
      ariaLabelledby={ID_MODAL_HEADER__SHOPPING__WEBCALLBACK_TRIGGER}
      closeLabel={t('common.close')}
      onClose={() => setIsOpen(false)}
    >
      <WebCallbackForm
        callbackStatus={callbackStatus}
        setCallbackStatus={setCallbackStatus}
        trackingData={trackingData}
      />
    </Modal>
  ) : null;

  const webCallbackDrawer = (
    <SidePanel
      ariaLabel={t('webCallback.title')}
      ariaLabelledby={ID_MODAL_HEADER__SHOPPING__WEBCALLBACK_TRIGGER}
      closeLabel={t('common.close')}
      isExpanded={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <div className="h-full w-full fadeIn">
        <WebCallbackForm
          callbackStatus={callbackStatus}
          setCallbackStatus={setCallbackStatus}
          trackingData={trackingData}
        />
      </div>
    </SidePanel>
  );

  const webCallbackPopin = isTablet ? webCallbackModal : webCallbackDrawer;

  return (
    <Fragment>
      {isAppMounted ? webCallbackPopin : null}
      <button
        type="button"
        className={classnames(themes[theme].thRoot, 'bg-transparent')}
        onClick={() => {
          setCallbackStatus(CALLBACK_STATUS.UNSCHEDULED);
          setIsOpen(true);
          if (isOpen === false && trackingData) {
            track({
              categoryGA: pageContext.pageId,
              labelGA: WEB_CALLBACK_OPEN,
              ...trackingData,
            });
          }
        }}
      >
        {children}
      </button>
    </Fragment>
  );
}
